import type { MouseEvent } from 'react';
import { Link as RouterLink, useMatch } from 'react-router-dom';
import { config } from '@/config';
import { usePageExperienceContextOptional } from '@/features/experiences/contexts/PageExperienceContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useIsAtLeastBreakpoint } from '@/hooks/useIsBreakpoint';
import { useIsMagic } from '@/hooks/useIsMagic';
import {
  chakra,
  Flex,
  Icon,
  Icons,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  type SystemStyleObject,
} from '@/ui';
import { useIsExperiencePage } from './useIsExperiencePage';
import { useIsHeaderNavVisible } from './useIsHeaderNavVisible';

// Served by the marketing site through a server proxy rather than by this app,
// so the nav links to them as plain URLs instead of router routes.
const DATES_URL = '/dates';
const SCHOOLS_URL = '/schools';
const TEMPLATES_URL = '/gallery';

const IOS_APP_URL = 'https://apps.apple.com/us/app/agenda-hero/id6480013905';
const ANDROID_APP_URL =
  'https://play.google.com/store/apps/details?id=com.agendahero.app';

const mobileAppLinks = [
  { label: 'iOS', href: IOS_APP_URL },
  { label: 'Android', href: ANDROID_APP_URL },
];

const externalLinkProps = { target: '_blank', rel: 'noopener noreferrer' };

const EMPHASIS_WEIGHT = 600;

const emphasis = {
  color: 'ink.strong',
  textDecoration: 'none',
  '& [data-nav-label-text]': {
    fontWeight: EMPHASIS_WEIGHT,
  },
  '& [data-nav-label]::after': {
    bg: 'currentColor',
  },
};

const navItemStyles: SystemStyleObject = {
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  h: { base: 7, md: 8 },
  px: { base: 1.5, md: 2 },
  color: 'ink.soft',
  fontSize: { base: 'ah-control', md: 'sm' },
  fontWeight: 500,
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  _hover: emphasis,
  '&[data-active]': emphasis,
  _focusVisible: { boxShadow: 'outline', borderRadius: 'ah-md' },
};

type NavLabelProps = {
  label: string;
};

// Reserves the bold width so emphasizing an item doesn't shift its neighbors.
const NavLabel = ({ label }: NavLabelProps) => (
  <chakra.span
    data-nav-label
    display="grid"
    position="relative"
    _after={{
      bg: 'transparent',
      content: '""',
      h: '2px',
      left: 0,
      pointerEvents: 'none',
      position: 'absolute',
      right: 0,
      top: 'calc(100% - 1px)',
    }}
  >
    <chakra.span display="block">
      <chakra.span data-nav-label-text lineHeight={0}>
        {label}
      </chakra.span>
    </chakra.span>
    <chakra.span
      aria-hidden
      fontWeight={EMPHASIS_WEIGHT}
      h={0}
      visibility="hidden"
    >
      {label}
    </chakra.span>
  </chakra.span>
);

type ItemProps = {
  label: string;
  isActive?: boolean;
  // An in-app route, rendered as a client-side Link.
  to?: string;
  // A URL the server owns (e.g. a proxied marketing page), rendered as a plain
  // anchor so the browser performs a real navigation rather than a router match.
  href?: string;
  isExternal?: boolean;
  // Looks and reads the same, but a click goes nowhere: the item's own route
  // is already open, and following the link again would only drop its state.
  isInert?: boolean;
  onClick?: () => void;
};

const NavItem = ({
  label,
  isActive = false,
  to,
  href,
  isExternal = false,
  isInert = false,
  onClick,
}: ItemProps) => {
  const linkProps = href
    ? { href, ...(isExternal && externalLinkProps) }
    : { as: RouterLink, to };

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (isInert) {
      event.preventDefault();
      return;
    }
    onClick?.();
  };

  return (
    <Link
      {...linkProps}
      aria-current={isActive ? 'page' : undefined}
      data-active={isActive || undefined}
      sx={navItemStyles}
      onClick={handleClick}
    >
      <NavLabel label={label} />
    </Link>
  );
};

const MobileAppsMenu = () => (
  <Menu isLazy placement="bottom-start">
    <MenuButton sx={navItemStyles}>
      <Flex align="center" gap={1}>
        <NavLabel label="Mobile Apps" />
        <Icon as={Icons.ChevronDown} boxSize={4} />
      </Flex>
    </MenuButton>
    <Portal>
      <MenuList minW="140px">
        {mobileAppLinks.map(({ label, href }) => (
          <MenuItem as="a" href={href} key={label} {...externalLinkProps}>
            {label}
          </MenuItem>
        ))}
      </MenuList>
    </Portal>
  </Menu>
);

export const HeaderNav = () => {
  const isVisible = useIsHeaderNavVisible();
  const { trackEvent } = useAnalytics();
  const pageExperience = usePageExperienceContextOptional();
  const { isMagic, isMagicLandingPage } = useIsMagic();
  const isLibrary = useMatch('/dashboard') !== null;
  const isExperiencePage = useIsExperiencePage();
  const isLargeScreen = useIsAtLeastBreakpoint('lg');

  if (!isVisible) {
    return null;
  }

  return (
    <Flex
      align="center"
      flexShrink={isExperiencePage ? 0 : { base: 1, md: 0 }}
      gap={{ base: 0.5, md: 1 }}
      minW={0}
      ml={{ md: 4 }}
      overflowX="auto"
      sx={{
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      <NavItem
        isActive={isLibrary}
        label="Library"
        to="/dashboard"
        // The tracking event is experience-scoped, so don't emit it from
        // other surfaces that render the header (e.g. the library page).
        onClick={
          pageExperience
            ? () => trackEvent('experience:click nav-library')
            : undefined
        }
      />
      {!isExperiencePage && (
        <>
          <NavItem
            isActive={isMagic || isMagicLandingPage}
            // On /magic itself a click would land on a bare /magic: its
            // results, chat and picked output in the URL all gone.
            isInert={isMagic}
            label="Magic"
            to="/magic"
          />
          <NavItem href={DATES_URL} label="Dates" />
          <NavItem href={SCHOOLS_URL} label="Schools" />
          <NavItem href={TEMPLATES_URL} label="Templates" />
          {isLargeScreen && (
            <>
              <MobileAppsMenu />
              <NavItem
                href={config.magic.chromeExtensionUrl}
                isExternal
                label="Chrome Extension"
              />
            </>
          )}
        </>
      )}
    </Flex>
  );
};
